import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface CenterNotification {
  id: string
  type: 'ticket' | 'chat' | 'sla' | 'system' | 'info'
  title: string
  body: string
  timestamp: number
  read: boolean
  snoozedUntil?: number | null
  actionRoute?: string
  icon?: string
}

export const useNotificationCenterStore = defineStore('notificationCenter', () => {
  const notifications = ref<CenterNotification[]>([])
  const isOpen = ref(false)
  const STORAGE_KEY = 'vhq_notification_center'
  const MAX_NOTIFICATIONS = 100

  // Load from localStorage on init
  function loadFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        notifications.value = JSON.parse(stored)
        // Prune old (>7 days) and un-snooze expired
        const now = Date.now()
        const weekAgo = now - 7 * 24 * 60 * 60 * 1000
        notifications.value = notifications.value
          .filter(n => n.timestamp > weekAgo)
          .map(n => ({
            ...n,
            snoozedUntil: n.snoozedUntil && n.snoozedUntil < now ? null : n.snoozedUntil
          }))
      }
    } catch { /* ignore corrupt storage */ }
  }

  function saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications.value.slice(0, MAX_NOTIFICATIONS)))
    } catch { /* storage full */ }
  }

  function add(notification: Omit<CenterNotification, 'id' | 'timestamp' | 'read'>) {
    const item: CenterNotification = {
      ...notification,
      id: Date.now().toString(36) + Math.random().toString(36).substr(2, 6),
      timestamp: Date.now(),
      read: false,
    }
    notifications.value.unshift(item)
    if (notifications.value.length > MAX_NOTIFICATIONS) {
      notifications.value = notifications.value.slice(0, MAX_NOTIFICATIONS)
    }
    saveToStorage()
    return item
  }

  function markRead(id: string) {
    const n = notifications.value.find(n => n.id === id)
    if (n) { n.read = true; saveToStorage() }
  }

  function markAllRead() {
    notifications.value.forEach(n => n.read = true)
    saveToStorage()
  }

  function snooze(id: string, durationMs: number) {
    const n = notifications.value.find(n => n.id === id)
    if (n) { n.snoozedUntil = Date.now() + durationMs; saveToStorage() }
  }

  function dismiss(id: string) {
    notifications.value = notifications.value.filter(n => n.id !== id)
    saveToStorage()
  }

  function clearAll() {
    notifications.value = []
    saveToStorage()
  }

  function toggle() { isOpen.value = !isOpen.value }
  function open() { isOpen.value = true }
  function close() { isOpen.value = false }

  const activeNotifications = computed(() => {
    const now = Date.now()
    return notifications.value.filter(n => !n.snoozedUntil || n.snoozedUntil < now)
  })

  const unreadCount = computed(() => {
    const now = Date.now()
    return notifications.value.filter(n => !n.read && (!n.snoozedUntil || n.snoozedUntil < now)).length
  })

  const grouped = computed(() => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const yesterday = today - 86400000

    const groups: { label: string; items: CenterNotification[] }[] = [
      { label: 'Today', items: [] },
      { label: 'Yesterday', items: [] },
      { label: 'Earlier', items: [] },
    ]

    for (const n of activeNotifications.value) {
      if (n.timestamp >= today) groups[0].items.push(n)
      else if (n.timestamp >= yesterday) groups[1].items.push(n)
      else groups[2].items.push(n)
    }

    return groups.filter(g => g.items.length > 0)
  })

  loadFromStorage()

  return {
    notifications, isOpen, unreadCount, activeNotifications, grouped,
    add, markRead, markAllRead, snooze, dismiss, clearAll,
    toggle, open, close,
  }
})
